import { Observable, interval, of, throwError, zip } from "rxjs";
import { every, map, mergeMap, retryWhen, switchMap, tap } from "rxjs/operators";
import _ from 'lodash'

import { NewRecolementIdNotFoundError, RecolementNotLinkedToAuditCampaignError, RecolementNotLinkedToItemError } from "./errors";
import { EntityType, MEDIA_SUBJECTS_KEYS, PushCachedValueResult, Record, RecordMaskField, SynchroMediaUpdates } from "@skinsoft/types";
import { auditCampaignSelectors, recordSelectors } from "@skinsoft/selectors";
import { SyncUpDependency } from "@skinsoft/classes-interfaces-definition";
import { auditRecord_recordMask } from "@skinsoft/record-mask-configs";

import { API_ENDPOINTS, DEFAULT_PROXY } from "@skinsoft/network";
import { AuditRecordOfflineDataUpdateCacher, OfflineDataFetcher, OfflineDataUpdateCacher } from "@skinsoft/offline";
import { RecordDataStore } from "@skinsoft/entity";
import { EntitySyncUpHandler } from "../../entity-sync-up-handler/entity-sync-up-handler";
import { ImageTransferHelper } from "@skinsoft/media-transfer-helper";
import { getOnlyValuesFromMaskFields } from "@skinsoft/utils";
import { WemiSyncUpHandler } from "../wemi";
import { genericRetryStrategy } from "@skinsoft/rxjs-utils";


declare const config
const NB_TRY_SYNC_UP = config.syncUp.auditRecord.maxNbRetry
const DELAY_TRY_SYNC_UP = config.syncUp.auditRecord.delayBetweenRetry

export class AuditRecordSyncUpHandler extends EntitySyncUpHandler<Record, Record> {
    private proxy = DEFAULT_PROXY
    
    public static getInstance() {
        if (!AuditRecordSyncUpHandler.instance) {
            AuditRecordSyncUpHandler.instance = new AuditRecordSyncUpHandler()
        }

        return AuditRecordSyncUpHandler.instance
    }

    public static install(Vue) {
        AuditRecordSyncUpHandler.getInstance()
    }

    private static instance: AuditRecordSyncUpHandler = null as any

    private constructor() {
        super(
            new AuditRecordOfflineDataUpdateCacher(),
            RecordDataStore.getInstance(),
            EntityType.AUDIT_RECORD
        )
    }

    getType(): EntityType {
        return this.type
    }

    getDependenciesIds(id: string): Observable<SyncUpDependency[]> {
        return super.getDependenciesIds(id).pipe(
            switchMap(superResult => {
                return zip(
                    of(superResult),
                    this.getCachedEntity(id)
                )
            }),
            switchMap(([superResult, cachedRecord]) => {
                let result: SyncUpDependency[] = []

                const itemId: string = recordSelectors.linkedItemId(cachedRecord as Record)
                if(itemId && OfflineDataUpdateCacher.isTemporaryId(itemId)) {
                    result.push({
                        id: itemId,
                        type: EntityType.WEMI
                    })
                }

                const auditCampaignId = cachedRecord.tempOfflineSchema?.auditCampaign
                if(
                    auditCampaignId && 
                    (
                        OfflineDataUpdateCacher.isTemporaryId(id) || 
                        OfflineDataUpdateCacher.isTemporaryId(auditCampaignId)
                    )
                ) {
                    // We need to wait for the synchro of the audit campaign if the audit record was created 
                    // offline because the audit record will be created when adding a new item to the campaign. 
                    // In case of an already existing audit record we already have the id of the campaign, so 
                    // no need to wait for the synchronysation of the campaign.
                    // The test OfflineDataUpdateCacher.isTemporaryId(auditCampaignId) is just a security 
                    // but should not be necessary 
                    result.push({
                        id: auditCampaignId,
                        type: EntityType.AUDIT_CAMPAIGN
                    })
                }

                result = superResult.concat(result)

                return of(result)
            })
        )
    }

    getAndFormatModificationsBeforeMerge(localEntity: Record): Observable<Partial<Record>> {
        let res: Partial<Record> = {}

        if(localEntity) {
            const masks: RecordMaskField[] = auditRecord_recordMask.categories.flatMap(category => category.maskFields)

            const values = getOnlyValuesFromMaskFields(
                localEntity,
                masks,
            )

            for(const [key, value] of Object.entries(values)) {
                _.set(
                    res,
                    key,
                    value
                )
            }
            
            // prevents to edit inventory number offline
            _.unset(
                res,
                'recolementIdentificationSchema.numberIdentifier'
            )
        }

        return of(res)
    }

    createNewEntity(dataToCreate: SynchroMediaUpdates<Record>): Observable<Record> {
        // We fetch the audit campaign containing the current recolement.
        // A recolement has already been created when we added the object to
        // the campaign (during push of audit campaign). We need to find which 
        // recolement in the audit campaign correspond to the same item id as 
        // the local recolement that we are "creating" and update it with the 
        // data we have locally
            
        const auditCampaignId = dataToCreate.body.tempOfflineSchema?.auditCampaign
        let itemId = recordSelectors.linkedItemId(dataToCreate.body)

        if(itemId && OfflineDataFetcher.isTemporaryId(itemId)) {
            itemId = WemiSyncUpHandler.getInstance().getNewId(itemId)
        }

        if(!auditCampaignId) {
            return throwError(new RecolementNotLinkedToAuditCampaignError())
        }
        if(!itemId) {
            return throwError(new RecolementNotLinkedToItemError())
        }

        let newId: string | null = null  
        
        // we try fetching the auditCampaign + searching a recolement from the auditCampaign that match
        return of(null).pipe(
            switchMap(() => {
                return this.proxy.fetch(API_ENDPOINTS.auditProject + `/${auditCampaignId}`)
            }),
            switchMap(auditCampaign => {
                return this.dataStore.searchAll({
                    page: 0,
                    pageSize: 1,
                    types: ['Recolement'],
                    filteredIds: auditCampaignSelectors.auditRecords(auditCampaign),
                    advancedSearches: [{
                        leftCriteria: {
                            entityField: '5d5d3797bbffbb20bbcca5fa',// linked item
                            operator: 'equals',
                            type: 'criteria',
                            value: {
                                completeSearch: false,
                                refId: itemId,
                                type: 'ref'
                            }
                        },
                        linkOperator: null,
                        rightCriteria: null,
                        type: 'requestSearch'
                    }],
                })
            }),
            switchMap(recolementSearchResult => {
                if(recolementSearchResult?.totalSize) {
                    return of(recolementSearchResult)
                }

                throw new NewRecolementIdNotFoundError()
            }),
            retryWhen(genericRetryStrategy({
                maxRetryAttempts: NB_TRY_SYNC_UP,
                scalingDuration: [DELAY_TRY_SYNC_UP],
                excludedStatusCodes: []
            })),
            switchMap(recolementSearchResult => {
                newId = recolementSearchResult.allData[0].id
                return this.dataStore.fetchOne(recolementSearchResult.allData[0].id)
            }),
        )
    }

    applyAfterPushOperations(pushResult: PushCachedValueResult, dataPushed: SynchroMediaUpdates<Record>, additionalData?: any): Observable<PushCachedValueResult> {
        return of(pushResult)/*this.handleReplaySubjects(pushResult, dataPushed, additionalData)*/.pipe(
            tap(() => {
                try {
                    const newRecordId = this.synchronizationMap[dataPushed.id] ?? dataPushed.id
                    const recordState = recordSelectors.currenStateValue(dataPushed.body);
                    (this.dataStore as RecordDataStore).transitionRecord(newRecordId, recordState, undefined, true)
                } catch(err) {
                    
                }
            })
        )
    }

    handleReplaySubjects(pushResult: PushCachedValueResult, dataToPush: SynchroMediaUpdates<Record>, additionalData?: any): Observable<PushCachedValueResult> {
        let recordId = dataToPush.id
        if(OfflineDataFetcher.isTemporaryId(dataToPush.id)) {
            if(pushResult.newId) {
                recordId = pushResult.newId
            }
            else {
                const recordIdFromSyncMap = this.synchronizationMap[dataToPush.id]
                if(recordIdFromSyncMap && recordIdFromSyncMap != "inProgress") {
                    recordId = recordIdFromSyncMap
                }
            }
        }
        
        if(recordId) {
            const imageSubject = dataToPush.mediaSubjects[MEDIA_SUBJECTS_KEYS.image]

            if(imageSubject) {
                let newFirstMediaId: string | null = null

                const timer = interval(1000)

                // combination zip + timer = time output from another source
                return zip(imageSubject, timer).pipe(
                    map(([replaySubjectContent, timer]) => replaySubjectContent),
                    mergeMap((replaySubjectContent): Observable<PushCachedValueResult> => {
                        if(replaySubjectContent.newMediaId) {
                            return (this.dataStore as RecordDataStore).addMedia(recordId, replaySubjectContent.newMediaId).pipe(
                                tap(() => {
                                    ImageTransferHelper.getInstance().deleteImageInCache(replaySubjectContent.newMediaId as string)
                                    
                                    if(replaySubjectContent.cacheKeys) {
                                        for(const cacheKey of replaySubjectContent.cacheKeys) {
                                            if(dataToPush.cachedMedias?.first && cacheKey === dataToPush.cachedMedias.first) {
                                                newFirstMediaId = replaySubjectContent.newMediaId as string
                                            }

                                            ImageTransferHelper.getInstance().deleteImageInCache(cacheKey)
                                        }
                                    }
                                }),
                                tap(() => {
                                    ImageTransferHelper.getInstance().addImageToReporterSubAlbum(replaySubjectContent.newMediaId)
                                }),
                                switchMap(() => of({
                                    success: true
                                })),
                            )
                        }

                        return of({ success: false })
                    }),
                    every((result: PushCachedValueResult) => result.success),
                    switchMap(allSucceded => {
                        if(newFirstMediaId) {
                            (this.dataStore as RecordDataStore).updateFirstMedia(recordId, newFirstMediaId).subscribe()
                        }

                        return of({ success: allSucceded })
                    })
                )
            } else {
                return of(pushResult)
            }
        }

        return of({ success: false })        
    }
}